import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions';
import './Complaints.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const Complaints = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    roomNo: '',
    complaint: '',
    contactNo: ''
  });
  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [canReadAllComplaints, setCanReadAllComplaints] = useState(false);

  useEffect(() => {
    const checkPermissions = () => {
      const societyId = localStorage.getItem('societyId');
      const hasReadPermission = hasPermission('complaint:read', societyId);
      const hasAllReadPermission = hasPermission('all:complaint:read', societyId);
      setCanReadAllComplaints(hasReadPermission || hasAllReadPermission);
    };

    checkPermissions();
  }, []);

  useEffect(() => {
    fetchComplaints();
  }, [canReadAllComplaints]);

  useEffect(() => {
    if (isModalOpen) {
      fetchRooms();
    }
  }, [isModalOpen]);

  const fetchRooms = async () => {
    try {
      setLoadingRooms(true);
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      if (!societyId) {
        throw new Error('No society selected');
      }

      const response = await fetch(
        `${API_URL}/api/v1/auth/rooms?access_token=${encodeURIComponent(token)}&org_id=${encodeURIComponent(societyId)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch rooms');
      }

      const data = await response.json();
      if (data.status && data.data?.RoomNos) {
        setRooms(data.data.RoomNos);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      setError('Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const fetchComplaints = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');
      const profileData = JSON.parse(localStorage.getItem('profileData') || '{}');

      if (!societyId) {
        throw new Error('No society selected');
      }

      let url = `${API_URL}/api/v1/auth/complaints?access_token=${encodeURIComponent(token)}&org_id=${encodeURIComponent(societyId)}`;
      
      const hasAllReadPermission = hasPermission('all:complaint:read', societyId);
      if (!hasAllReadPermission && profileData.uid) {
        url += `&uid=${encodeURIComponent(profileData.uid)}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch complaints');
      }

      const data = await response.json();
      if (data.status && data.complaints) {
        setComplaints(data.complaints);
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
      setError('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const token = localStorage.getItem('accessToken');
      const societyId = localStorage.getItem('societyId');

      const response = await fetch(
        `${API_URL}/api/v1/auth/complaints?access_token=${encodeURIComponent(token)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            org_id: societyId,
            room_no: formData.roomNo,
            complaint_text: formData.complaint,
            contact_no: formData.contactNo
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create complaint');
      }

      const data = await response.json();
      if (data.status) {
        setIsModalOpen(false);
        setFormData({
          roomNo: '',
          complaint: '',
          contactNo: ''
        });
        fetchComplaints(); // Refresh complaints list
      }
    } catch (error) {
      console.error('Error creating complaint:', error);
      setError('Failed to create complaint');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return <div className="complaints-loading">Loading complaints...</div>;
  }

  return (
    <div className="complaints-container">
      <div className="complaints-header">
        <h2>{canReadAllComplaints ? 'All Complaints' : 'My Complaints'}</h2>
        <button 
          className="raise-complaint-btn"
          onClick={() => setIsModalOpen(true)}
        >
          <i className="fas fa-plus"></i>
          Raise Complaint
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="complaints-list">
        {complaints.map((complaint) => (
          <div key={complaint.id} className="complaint-card">
            <div className="complaint-header">
              <span className="complaint-id">#{complaint.id}</span>
              <span className={`complaint-status status-${complaint.Status.toLowerCase()}`}>
                {complaint.Status}
              </span>
            </div>
            <div className="complaint-content">
              <p className="complaint-text">{complaint.complaint_text}</p>
              <div className="complaint-details">
                <span>Room: {complaint.RoomNo}</span>
                <span>Contact: {complaint.ContactNo}</span>
                <span>Description: {complaint.ComplaintText}</span>
                <span>Created: {formatDate(complaint.CreatedDate)}</span>
                <span>Last Updated: {formatDate(complaint.LastModifiedDate)}</span>
              </div>
            </div>
          </div>
        ))}
        {complaints.length === 0 && (
          <div className="no-complaints">
            <p>No complaints found</p>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Raise a Complaint</h3>
              <button 
                className="close-button"
                onClick={() => setIsModalOpen(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="complaint-form">
              <div className="form-group">
                <label htmlFor="roomNo">Room Number</label>
                {loadingRooms ? (
                  <div className="select-loading">Loading rooms...</div>
                ) : (
                  <select
                    id="roomNo"
                    name="roomNo"
                    value={formData.roomNo}
                    onChange={handleChange}
                    required
                    className="room-select"
                  >
                    <option value="">Select a room</option>
                    {rooms.map((room) => (
                      <option key={room} value={room}>
                        {room}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="complaint">Complaint</label>
                <textarea
                  id="complaint"
                  name="complaint"
                  value={formData.complaint}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Describe your complaint"
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactNo">Contact Number</label>
                <input
                  type="tel"
                  id="contactNo"
                  name="contactNo"
                  value={formData.contactNo}
                  onChange={handleChange}
                  required
                  placeholder="Enter contact number"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                >
                  Submit Complaint
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Complaints; 